import { useState } from "react";
import { createAccountCharge, deleteAccountCharge, updateAccountCharge, useAction } from "wasp/client/operations";
import { Trash2, Plus, Pencil } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../client/components/ui/dialog";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";

interface AccountCharge {
  id: string;
  name: string;
  amount: number;
  frequency: string;
  accountId: string | null;
  account?: { id: string; name: string } | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  billingDay?: number | null;
  source?: string | null;
}

interface AccountChargesManagerProps {
  charges: AccountCharge[];
  accountId: string | null;
  onMutated: () => void;
}

export default function AccountChargesManager({
  charges,
  accountId,
  onMutated,
}: AccountChargesManagerProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const createChargeFn = useAction(createAccountCharge);
  const deleteChargeFn = useAction(deleteAccountCharge);
  const updateChargeFn = useAction(updateAccountCharge);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<AccountCharge | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState<"monthly" | "annual" | "one_time">("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billingDay, setBillingDay] = useState("");
  const [loading, setLoading] = useState(false);

  const openCreate = () => {
    setEditingCharge(null);
    setName("");
    setAmount("");
    setFrequency("monthly");
    setStartDate("");
    setEndDate("");
    setBillingDay("");
    setDialogOpen(true);
  };

  const openEdit = (charge: AccountCharge) => {
    setEditingCharge(charge);
    setName(charge.name);
    setAmount(String(charge.amount));
    setFrequency(charge.frequency as "monthly" | "annual" | "one_time");
    setStartDate(charge.startDate ? new Date(charge.startDate).toISOString().split("T")[0] : "");
    setEndDate(charge.endDate ? new Date(charge.endDate).toISOString().split("T")[0] : "");
    setBillingDay(charge.billingDay ? String(charge.billingDay) : "");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCharge(null);
  };

  const handleAdd = async () => {
    if (!name.trim() || !amount) return;
    setLoading(true);
    try {
      await createChargeFn({
        accountId: accountId ?? null,
        name: name.trim(),
        amount: parseFloat(amount),
        frequency,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        billingDay: billingDay ? parseInt(billingDay, 10) : null,
      });
      closeDialog();
      toast({ title: t.tradingAccounts.chargeAdded });
      onMutated();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.chargeAddError, description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingCharge || !name.trim() || !amount) return;
    setLoading(true);
    try {
      await updateChargeFn({
        id: editingCharge.id,
        name: name.trim(),
        amount: parseFloat(amount),
        frequency,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        billingDay: billingDay ? parseInt(billingDay, 10) : null,
      });
      closeDialog();
      toast({ title: t.tradingAccounts.chargeUpdated });
      onMutated();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.chargeEditError, description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (charge: AccountCharge) => {
    // Une charge récurrente porte tout son historique : un premier clic
    // prévient, le second supprime.
    if (charge.frequency !== "one_time" && confirmDeleteId !== charge.id) {
      setConfirmDeleteId(charge.id);
      toast({ title: t.tradingAccounts.chargeDeleteRecurringConfirm });
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteChargeFn({ id: charge.id });
      toast({ title: t.tradingAccounts.chargeDeleted });
      onMutated();
    } catch {
      toast({ title: t.tradingAccounts.chargeDeleteError, variant: "destructive" });
    }
  };

  const frequencyLabel = (f: string) => {
    if (f === "monthly") return t.tradingAccounts.chargeMonthly;
    if (f === "annual") return t.tradingAccounts.chargeAnnual;
    return t.tradingAccounts.chargeOneTime;
  };

  return (
    <div className="space-y-3">
      {charges.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t.tradingAccounts.noCharges}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left pb-2 pr-3 font-medium">{t.tradingAccounts.chargeName}</th>
                <th className="text-right pb-2 pr-3 font-medium whitespace-nowrap">{t.tradingAccounts.chargeAmount}</th>
                <th className="text-left pb-2 pr-3 font-medium whitespace-nowrap">{t.tradingAccounts.chargeFrequency}</th>
                <th className="text-left pb-2 pr-3 font-medium whitespace-nowrap">{t.tradingAccounts.chargeStartDate}</th>
                <th className="text-right pb-2 font-medium">{t.propFirmManager.wallet.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => (
                <tr key={charge.id} className="border-b border-border">
                  <td className="py-2.5 pr-3 font-medium max-w-[140px]" title={charge.name}>
                    <span className="truncate block">{charge.name}</span>
                    {charge.source === "prop_firm_subscription" && (
                      <span className="mt-0.5 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-normal text-primary">
                        {t.tradingAccounts.chargeSourceSubscription}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono whitespace-nowrap">{charge.amount} $</td>
                  <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{frequencyLabel(charge.frequency)}</td>
                  <td className="py-2.5 pr-3 text-muted-foreground font-mono whitespace-nowrap">
                    {charge.startDate && charge.startDate !== "" ? (
                      <>
                        {new Date(charge.startDate).toLocaleDateString()}
                        {charge.billingDay ? ` · J${charge.billingDay}` : ""}
                        {charge.endDate ? ` → ${new Date(charge.endDate).toLocaleDateString()}` : ""}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openEdit(charge)}
                      className="p-1 rounded hover:bg-secondary/10 text-muted-foreground hover:text-secondary transition-colors"
                      title={t.tradingAccounts.chargeEdit}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(charge)}
                      className={`p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-colors ${confirmDeleteId === charge.id ? "text-destructive bg-destructive/10" : "text-muted-foreground"}`}
                      title={t.tradingAccounts.chargeDelete}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button type="button" size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={openCreate}>
        <Plus className="h-3.5 w-3.5" />
        {t.tradingAccounts.chargeAddTitle}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCharge ? t.tradingAccounts.chargeEditTitle : t.tradingAccounts.chargeAddTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-xs">{t.tradingAccounts.chargeName}</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.tradingAccounts.chargeNamePlaceholder}
                className="h-8 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t.tradingAccounts.chargeAmount}</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t.tradingAccounts.chargeAmountPlaceholder}
                  className="h-8 text-sm"
                  min={0}
                  step={0.01}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t.tradingAccounts.chargeFrequency}</Label>
                <Select value={frequency} onValueChange={(v) => {
                  const f = v as "monthly" | "annual" | "one_time";
                  setFrequency(f);
                  if (f !== "monthly") setBillingDay("");
                  if (f === "one_time") setEndDate("");
                }}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t.tradingAccounts.chargeMonthly}</SelectItem>
                    <SelectItem value="annual">{t.tradingAccounts.chargeAnnual}</SelectItem>
                    <SelectItem value="one_time">{t.tradingAccounts.chargeOneTime}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t.tradingAccounts.chargeStartDate}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              {frequency === "monthly" && (
                <div className="space-y-1">
                  <Label className="text-xs">{t.tradingAccounts.chargeBillingDay}</Label>
                  <Input
                    type="number"
                    value={billingDay}
                    onChange={(e) => setBillingDay(e.target.value)}
                    placeholder={t.tradingAccounts.chargeBillingDayPlaceholder}
                    className="h-8 text-sm"
                    min={1}
                    max={31}
                  />
                </div>
              )}
            </div>

            {frequency !== "one_time" && (
              <div className="space-y-1">
                <Label className="text-xs">{t.tradingAccounts.chargeEndDate}</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-8 text-sm"
                />
                <p className="text-[11px] text-muted-foreground">{t.tradingAccounts.chargeEndDateHint}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={closeDialog} disabled={loading}>
              {t.tradingAccounts.chargeCancel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={editingCharge ? handleUpdate : handleAdd}
              disabled={loading || !name.trim() || !amount || isNaN(parseFloat(amount))}
            >
              {editingCharge ? t.tradingAccounts.chargeSave : t.tradingAccounts.chargeAddTitle}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
